import axios, { AxiosResponse } from 'axios';
import { useState, useEffect, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { commentFeedReducer, SortOrder } from '../models/comments/feeds';
import {
  Comment,
  CommentPostBatchResponse,
  CommentWithReplies,
  isCommentWithReplies,
} from '../models/comments/types';
import {
  fetchCommentById,
  fetchCommentsByIds,
  fetchManyComments,
  fetchReplies as fetchRepliesService,
} from '../services/comment';
import { Feature, getFeatureFlag } from '../models/features/features';
import { usePatronSelector } from './use-patron-selector';
import {
  createToastMessage,
  ToastMessageType,
  useToastMessages,
} from '../components/v2/toaster/toaster';

type UseCommentFeedProps = {
  contentId: number;
  sortOrder: SortOrder;
  commentId?: string;
  fetchSingleThreadReplies?: boolean; // Fetch all replies if the commentId is provided
};

const MAX_ERROR_RETRIES = 5;

const useCommentFeed = ({
  contentId,
  sortOrder,
  commentId,
  fetchSingleThreadReplies,
}: UseCommentFeedProps) => {
  const [comments, dispatch] = useReducer(commentFeedReducer, []);

  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>();
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [pagesFetched, setPagesFetched] = useState<number[]>([]);
  const [errorRetryCount, setErrorRetryCount] = useState(0);

  const [translateAllActive, setTranslateAllActive] = useState(false);

  const { t } = useTranslation();
  const { setToastMessage } = useToastMessages();

  const setMessage = useCallback(
    (message: ToastMessageType) => setToastMessage(createToastMessage(message)),
    [setToastMessage]
  );

  const handleFetchSuccess = useCallback(
    (res: AxiosResponse<CommentPostBatchResponse>, reset?: boolean) => {
      const newComments = res.data.data.map<Comment>((comment) => ({
        ...comment.attributes,
        id: comment.id,
      }));

      const curPage = res.data.meta.currentPage;
      const pageCount = res.data.meta.totalPages;

      setPagesFetched((prevPagesFetched) => {
        const newPagesFetched = reset
          ? [curPage]
          : [...prevPagesFetched, curPage];

        setHasNextPage(pageCount > 0 && !newPagesFetched.includes(pageCount));

        return newPagesFetched;
      });

      dispatch({
        type: reset ? 'SET_COMMENTS' : 'APPEND_COMMENTS',
        payload: { comments: newComments },
      });

      setPage(curPage);
      setErrorRetryCount(0);
      setIsFetching(false);
    },
    []
  );

  const handleFetchFailure = useCallback(() => {
    setMessage({ text: t('comments.errors.fetch'), type: 'error' });
    setErrorRetryCount((count) => count + 1);
    setIsFetching(false);
  }, [t]);

  const fetchComments = useCallback(
    async (reset?: boolean) => {
      setIsFetching(true);
      if (reset) {
        dispatch({ type: 'SET_COMMENTS', payload: { comments: [] } });
      }

      try {
        const res = await fetchManyComments({
          contentId,
          params: {
            page: reset ? 1 : page + 1,
            sort_order: sortOrder,
            translate: translateAllActive,
          },
        });

        handleFetchSuccess(res, reset);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          handleFetchFailure();
        } else {
          throw err;
        }
      } finally {
        setHasFetched(true);
      }
    },
    [
      contentId,
      handleFetchFailure,
      handleFetchSuccess,
      page,
      sortOrder,
      translateAllActive,
    ]
  );

  const fetchReplies = useCallback(
    async (comment: Comment | CommentWithReplies, reset?: boolean) => {
      setIsFetching(true);
      try {
        const res = await fetchRepliesService(contentId, comment.id, {
          translate: translateAllActive,
          page: isCommentWithReplies(comment)
            ? comment.repliesCurrentPage + 1
            : 1,
          page_size: 10,
          sort_order: sortOrder,
        });
        const replies = res.data.data.map<Comment>((reply) => ({
          ...reply.attributes,
          id: reply.id,
          replyToId: comment.id,
        }));

        const sortedReplies = replies;

        dispatch({
          type: 'UPDATE_COMMENT',
          payload: {
            comment: {
              ...comment,
              replies:
                reset || !isCommentWithReplies(comment)
                  ? sortedReplies
                  : comment.replies.concat(sortedReplies),
              repliesPageCount: res.data.meta.totalPages, // Pagination construct, not yet used
              repliesCurrentPage: res.data.meta.currentPage, // Pagination construct, not yet used
            } as CommentWithReplies,
          },
        });

        setIsFetching(false);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          handleFetchFailure();
        } else {
          throw err;
        }
      }
    },
    [contentId, translateAllActive, handleFetchFailure, sortOrder]
  );

  const fetchSingleComment = useCallback(
    async (commentId: string) => {
      try {
        const res = await fetchCommentById(contentId, commentId);
        let comment: Comment | CommentWithReplies = {
          ...res.data.data.attributes,
          id: res.data.data.id,
        };

        dispatch({
          type: 'SET_COMMENTS',
          payload: { comments: [comment] },
        });

        if (comment.replyToId !== null) {
          const parentRes = await fetchCommentById(
            contentId,
            comment.replyToId
          );

          const parentComment = {
            ...parentRes.data.data.attributes,
            id: parentRes.data.data.id,
          };

          comment = {
            ...parentComment,
            replies: [
              {
                ...comment,
                replyToId: parentComment.id,
              },
            ],
          };

          dispatch({
            type: 'SET_COMMENTS',
            payload: { comments: [comment] },
          });
        } else if (fetchSingleThreadReplies) {
          await fetchReplies(comment);
        }

        setPagesFetched([]);
        setIsFetching(false);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          handleFetchFailure();
        } else {
          throw err;
        }
      } finally {
        setHasFetched(true);
      }
    },
    [contentId, fetchReplies, fetchSingleThreadReplies, handleFetchFailure]
  );

  const addComment = (comment: Comment) => {
    if (comment.isEdited) {
      dispatch({ type: 'UPDATE_COMMENT', payload: { comment } });
    } else {
      dispatch({ type: 'ADD_COMMENT', payload: { comment, sortOrder } });
    }
  };

  const updateComment = (comment: Comment) => {
    dispatch({ type: 'UPDATE_COMMENT', payload: { comment } });
  };

  const removeComment = (comment: Comment) => {
    dispatch({ type: 'REMOVE_COMMENT', payload: { comment } });
  };

  //helper function to make updating a partial comment easier. kinda just using a stub to put off refactoring all of this.
  //could just set the value in the comment and call updateComment, but this makes it easier to understand the intent of the update code.
  const updateCommentData = (
    comment: Comment,
    updateValues: Partial<Comment>
  ) => {
    dispatch({
      type: 'UPDATE_COMMENT',
      payload: {
        comment: {
          id: comment.id,
          replyToId: comment.replyToId, //this is the main gotcha that this function helps us avoid
          ...updateValues,
        },
      },
    });
  };

  const updateCommentsData = (
    comments: Comment[],
    updateValues: Partial<Comment>
  ) => {
    dispatch({
      type: 'UPDATE_COMMENTS',
      payload: {
        comments: comments.map((comment) => ({
          id: comment.id,
          replyToId: comment.replyToId, //this is the main gotcha that this function helps us avoid
          ...updateValues,
        })),
      },
    });
  };

  const onTranslateCommentFail = useCallback(() => {
    setMessage({ text: t('comments.errors.fetch'), type: 'error' });
  }, [t]);

  const batchTranslateComments = useCallback(
    async (requestedComments: Comment[], translate: boolean) => {
      if (requestedComments.length === 0) {
        return;
      }

      try {
        updateCommentsData(requestedComments, {
          translationInProgress: true,
        });

        //fetch all comments with the new translation state
        const res = await fetchCommentsByIds(
          contentId,
          requestedComments.map((comment) => comment.id),
          {
            translate,
          }
        );

        const newComments = res.data.data.map((comment) => ({
          ...comment.attributes,
          id: comment.id,
        }));

        //update all comments with the new translation state
        dispatch({
          type: 'UPDATE_COMMENTS',
          payload: { comments: newComments },
        });
      } catch (e) {
        onTranslateCommentFail();
        throw e;
      } finally {
        //clean up translation 'in progress' state
        updateCommentsData(requestedComments, {
          translationInProgress: false,
        });
      }
    },
    [contentId, onTranslateCommentFail]
  );

  useEffect(() => {
    if (!commentId || errorRetryCount > MAX_ERROR_RETRIES) return;

    setIsFetching(true);
    setTimeout(() => fetchSingleComment(commentId), errorRetryCount * 1000);
  }, [commentId, errorRetryCount, fetchSingleComment]);

  useEffect(() => {
    if (
      isFetching ||
      commentId ||
      pagesFetched.includes(page) ||
      errorRetryCount > MAX_ERROR_RETRIES
    ) {
      return;
    }

    setIsFetching(true);
    setTimeout(() => fetchComments(true), errorRetryCount * 1000);
  }, [
    commentId,
    errorRetryCount,
    fetchComments,
    isFetching,
    page,
    pagesFetched,
  ]);

  useEffect(() => {
    // Reset page metadata when contentId or sortOrder changes
    setPage(0);
    setPagesFetched([]);
  }, [contentId, sortOrder]);

  return {
    page,
    comments,
    setMessage,
    hasNextPage,
    fetchNextPage: fetchComments,
    fetchReplies,
    isFetching: isFetching || !hasFetched,
    addComment,
    updateComment,
    updateCommentData,
    removeComment,
    batchTranslateComments,
    translateAllActive,
    setTranslateAllActive,
  };
};

export { SortOrder };
export default useCommentFeed;
